import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Navigation }      from "./components/navigation";
import { Header }          from "./components/header";
import { About }           from "./components/about";
import { Services }        from "./components/services";
import { Certifications }  from "./components/Certifications";
import { Products }        from "./components/Products";
import { Brands }          from "./components/Brands";
import { Distributor }     from "./components/Distributor";
import Gallery             from "./components/gallery";
import { Contact }         from "./components/contact";
import AdminUpload         from "./components/AdminUpload";
import Login               from "./components/Login";
import JsonData            from "./data/data.json";
import SmoothScroll        from "smooth-scroll";
import { Helmet }          from "react-helmet";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./App.css";
import NetworkStatus from "./components/NetworkStatus";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 400,
  speedAsDuration: true,
  easing: "easeInOutCubic",
});

const CLOUD_NAME = "dbgzzcwvr";
const GALLERY_TAG = "nb-gallery";

async function fetchCloudinaryMedia() {
  const cb = Date.now();
  const opts = { cache: "reload", headers: { "Cache-Control": "no-cache, no-store, must-revalidate", Pragma: "no-cache" }, mode: "cors" };
  try {
    const [iR, vR] = await Promise.all([
      fetch(`https://res.cloudinary.com/${CLOUD_NAME}/image/list/${GALLERY_TAG}.json?cb=${cb}`, opts),
      fetch(`https://res.cloudinary.com/${CLOUD_NAME}/video/list/${GALLERY_TAG}.json?cb=${cb}`, opts),
    ]);
    const iJ = iR.ok ? await iR.json() : { resources: [] };
    const vJ = vR.ok ? await vR.json() : { resources: [] };
    const imgUrl = (r) => `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/f_auto,q_auto/${r.version ? `v${r.version}/` : ""}${encodeURIComponent(r.public_id + "." + (r.format || "jpg"))}?cb=${cb}`;
    const vidUrl = (r) => `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/${r.version ? `v${r.version}/` : ""}${encodeURIComponent(r.public_id + "." + (r.format || "mp4"))}?cb=${cb}`;
    return [
      ...(iJ.resources || []).map((r) => ({ url: imgUrl(r), type: "image" })),
      ...(vJ.resources || []).map((r) => ({ url: vidUrl(r), type: "video" })),
    ];
  } catch (e) { console.error("Cloudinary fetch error:", e); return []; }
}

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const [media, setMedia] = useState([]);
  const bcRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { setLandingPageData(JsonData); fetchCloudinaryMedia().then(setMedia); }, []);

  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel("nb-media");
      bcRef.current.onmessage = (m) => { if (m?.data?.type === "refresh") fetchCloudinaryMedia().then(setMedia); };
    } catch { bcRef.current = null; }
    return () => { try { bcRef.current?.close(); } catch {} };
  }, []);

  useEffect(() => {
    const onFocus = () => fetchCloudinaryMedia().then(setMedia);
    const onVis = () => { if (document.visibilityState === "visible") fetchCloudinaryMedia().then(setMedia); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    const onStorage = (e) => { if (e.key === "nb_media_bump") fetchCloudinaryMedia().then(setMedia); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    pollRef.current = setInterval(() => fetchCloudinaryMedia().then(setMedia), 60000);
    return () => clearInterval(pollRef.current);
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <div>
            <Helmet>
              <title>Netra Bharat Industries | Kitchenware Manufacturer India — Utensy Brand</title>
              <meta name="description" content="Netra Bharat Industries — manufacturer of Tri-Ply cookware, pressure cookers & gas stoves. OEM, bulk supply, dealer, distributor & export enquiries welcome. Supplying India and international markets including Nepal." />
              <meta name="keywords" content="kitchenware manufacturer India, pressure cooker manufacturer, Tri-Ply cookware manufacturer, OEM kitchenware manufacturer, gas stove manufacturer Delhi, cookware supplier India, kitchenware exporter India, Nepal kitchenware supplier, wholesale cookware supplier, Utensy, Netra Bharat" />
              <meta property="og:title" content="Netra Bharat Industries | Premium Kitchenware Manufacturer" />
              <meta property="og:description" content="Tri-Ply cookware, pressure cookers & gas stoves — manufactured in New Delhi. OEM, bulk, dealer & export enquiries welcome." />
              <meta property="og:image" content="https://res.cloudinary.com/dbgzzcwvr/image/upload/w_1200,h_630,c_pad,b_white/v1756632676/icon_xqzodb.png" />
              <meta property="og:url" content="https://netrabharat.com/" />
              <meta property="og:type" content="website" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:image" content="https://res.cloudinary.com/dbgzzcwvr/image/upload/w_1200,h_630,c_pad,b_white/v1756632676/icon_xqzodb.png" />
              <script type="application/ld+json">{`{
                "@context":"https://schema.org","@type":"Organization",
                "name":"Netra Bharat Industries Pvt. Ltd.",
                "url":"https://netrabharat.com",
                "logo":"https://res.cloudinary.com/dbgzzcwvr/image/upload/v1756632676/icon_xqzodb.png",
                "address":{"@type":"PostalAddress","streetAddress":"79, Pocket L, DSIIDC Industrial Area, Sector 1, Bawana","addressLocality":"New Delhi","postalCode":"110039","addressCountry":"IN"},
                "telephone":"+911141681813","email":"info@netrabharat.com"
              }`}</script>
            </Helmet>
            <Navigation />
            <Header       data={landingPageData.Header} />
            <About        data={landingPageData.About} />
            <Services     data={landingPageData.Services} />
            <Certifications />
            <Products />
            <Brands />
            <Distributor />
            <Gallery      media={media} />
            <Contact      data={landingPageData.Contact} />
            <NetworkStatus />
          </div>
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<AdminUpload setMedia={setMedia} />} />
      </Routes>
    </Router>
  );
};

export default App;
